"use client";

import { ExternalLink } from "lucide-react";
import { DateTime } from "luxon";

import { ENTP_REFERRAL_TEL_ENC_ASSIGNMENT_DEFAULT } from "@/configuration/groups/entpartners/configuration";
import {
  ENTP_CHICAGO_ECW_LOGIN_URL,
  ENTP_REFERRAL_APPOINTMENT_DISPLAY_TIMEZONE,
  ENTP_SUBOTO_REFERRAL_TEL_ENC_ASSIGNEE,
  getEntpProviderDisplayNameFromEhrId,
} from "@/configuration/groups/entpartners/referral-instructions";
import { ENTS_REFERRAL_ASSIGNEE } from "@/configuration/groups/ents/configuration";
import { buildEntsTelEncMessage, ENTS_TEL_ENC_REASON } from "@/configuration/groups/ents/tel-enc-message";
import {
  MENT_ECW_LOGIN_URL,
  MENT_REFERRAL_FACILITY,
  MENT_REFERRAL_FRONT_OFFICE_ASSIGNEE,
} from "@/configuration/groups/ment/configuration";
import {
  ENTP_SCI_ECW_LOGIN_URL,
  ENTP_SCI_NCH_NEUROSCIENCES_SYNC,
  isEntpSciNchNeurosciencesSyncReferral,
} from "@/configuration/groups/sci/referral-instructions";
import { SIENT_REFERRAL_ASSIGNEE } from "@/configuration/groups/sient/configuration";
import { RecommendationType } from "@/shared";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { SchedulingRecommendationVerification } from "@/src/components/comms/referral-scheduling-recommendation";
import { SchedulingEligibility } from "@/src/components/comms/scheduling-eligibility";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/src/components/shadcn/ui/table";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { ValerieCard } from "@/src/components/valerie-card";
import {
  useReferralSchedulingRecommendation,
  usePatientAppointments,
} from "@/src/lib/data-fetchers/scheduling-fetchers";
import { formatDateForFilename, formatDateTimeWithTimezoneForDisplay } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";
import { getPersonName } from "@/src/models/person";
import { isRecommendedForScheduling } from "@/src/models/scheduling-recommendation";

interface EntPartnersReferralInstructionsProps {
  referral: Referral;
  schedulingVerificationTaskEnabled?: boolean;
}

function DefaultENTPReferralMessage({
  referral,
  groupName,
  assignedTo = ENTP_REFERRAL_TEL_ENC_ASSIGNMENT_DEFAULT,
  customReferralMessage: customReferralMessageOverride,
  ecwUrl = ENTP_CHICAGO_ECW_LOGIN_URL,
  showSchedulingRecommendation = false,
  schedulingVerificationTaskEnabled = false,
}: {
  referral: Referral;
  groupName: string;
  assignedTo?: string;
  customReferralMessage?: string;
  ecwUrl?: string | null;
  showSchedulingRecommendation?: boolean;
  schedulingVerificationTaskEnabled?: boolean;
}) {
  const formattedDateOfReferral = formatDateForFilename(referral.dateOfReferral);
  const providerName = referral.receivingProvider?.familyName ? getPersonName(referral.receivingProvider) : "ENT";
  const customReferralMessage =
    customReferralMessageOverride ??
    `Please schedule this patient. Referred to ${providerName} for ${referral.reasonForReferral || ""}`;

  const patient = referral.patient;
  const { appointments, isPending: isAppointmentsPending } = usePatientAppointments(patient?.id);
  const { recommendation: referralSchedulingRecommendation, isPending: isReferralSchedulingRecommendationPending } =
    useReferralSchedulingRecommendation(referral.id, true /* schedulingEnabled */);

  const startOfDay = DateTime.now().setZone(ENTP_REFERRAL_APPOINTMENT_DISPLAY_TIMEZONE).startOf("day");
  const upcomingAppointments = appointments?.filter((appointment) => {
    const appointmentDate = DateTime.fromISO(appointment.appointmentDatetime || "").setZone(
      ENTP_REFERRAL_APPOINTMENT_DISPLAY_TIMEZONE
    );

    return (
      appointmentDate.isValid &&
      appointmentDate >= startOfDay &&
      appointment.ehrType &&
      appointment.ehrType !== "new_patient_checkin"
    );
  });

  const renderSchedulingMessage = () => {
    const telEncInstruction = (
      <>
        Create a tel enc to <CopyableContent text={assignedTo} alwaysShow={true} /> with the following message:{" "}
        <CopyableContent text={customReferralMessage} alwaysShow={true} />
      </>
    );

    if (isAppointmentsPending) {
      return <span className="text-xs text-muted-foreground">Checking for upcoming appointments...</span>;
    }

    if (upcomingAppointments?.length) {
      return (
        <div className="flex flex-col gap-2 text-muted-foreground">
          <p>
            3. Scheduling the patient may not be needed. Upcoming appointment
            {upcomingAppointments.length > 1 ? "s" : ""} found:
          </p>
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingAppointments.map((appointment) => (
                <TableRow key={appointment.id}>
                  <TableCell>
                    <a
                      href={`/patients/${patient?.displayId}/appointments`}
                      className="flex items-center gap-1 hover:underline"
                    >
                      {formatDateTimeWithTimezoneForDisplay(
                        appointment.appointmentDatetime,
                        ENTP_REFERRAL_APPOINTMENT_DISPLAY_TIMEZONE,
                        false
                      )}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  </TableCell>
                  <TableCell>{getEntpProviderDisplayNameFromEhrId(appointment.providerEhrId)}</TableCell>
                  <TableCell>{appointment.ehrType}</TableCell>
                  <TableCell>{appointment.ehrReason || "Unknown Reason"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p>
            <span className="italic">If the appointment isn&apos;t valid:</span> {telEncInstruction}
          </p>
        </div>
      );
    }

    if (isReferralSchedulingRecommendationPending) {
      return <span className="text-xs text-muted-foreground">Checking for eligibility...</span>;
    }

    const isEligible =
      isRecommendedForScheduling(referralSchedulingRecommendation, RecommendationType.NEW_PATIENT) ||
      isRecommendedForScheduling(referralSchedulingRecommendation, RecommendationType.ESTABLISHED_PATIENT);

    const schedulingTitlePrefix = isEligible
      ? "3. Patient is eligible, send SMS outreach"
      : "3. Patient is not eligible, send tel enc message";

    return (
      <div className="relative text-muted-foreground">
        <div className="flex items-center gap-2">
          <p>{schedulingTitlePrefix}</p>
          <div className="inline-block relative top-[3px]">
            <SchedulingEligibility
              agenticRecommendation={referralSchedulingRecommendation?.agenticRecommendation}
              receivingProvider={referral.receivingProvider}
            />
          </div>
        </div>
        {isEligible ? (
          <div className="mt-1">
            <p>
              Confirm in eCW whether the patient already has a matching upcoming scheduled appointment. If not, click on
              the schedule link above to schedule a new appointment.
            </p>
            <p className="mt-2">
              <span className="font-semibold">If needed:</span> <br /> {telEncInstruction}
            </p>
          </div>
        ) : (
          <p className="mt-1">{telEncInstruction}</p>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <p className="font-medium text-center mb-4">{groupName} Referral Instructions:</p>

      <div className="text-muted-foreground flex flex-col gap-2">
        <p>
          1. Create or Update the patient in eCW
          {ecwUrl ? (
            <>
              {" via:"}
              <a href={ecwUrl} target="_blank" className="hover:bg-accent rounded p-1">
                Link
              </a>
            </>
          ) : null}
        </p>
        <p>
          2. Upload the file in Patient Docs, under the Referrals folder. Mark as reviewed. Name the file{" "}
          <CopyableContent text={`${formattedDateOfReferral} Referral`} />
        </p>

        {renderSchedulingMessage()}
      </div>

      {showSchedulingRecommendation && !schedulingVerificationTaskEnabled && (
        <SchedulingRecommendationVerification
          referralId={referral.id}
          recommendation={referralSchedulingRecommendation}
        />
      )}
    </div>
  );
}

function EntPartnersReferralCards({ referral }: EntPartnersReferralInstructionsProps) {
  return (
    <>
      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage!} type="Primary" isEditable />}
      {referral.secondaryCoverage && (
        <CoverageCard coverage={referral.secondaryCoverage!} type="Secondary" isEditable />
      )}
    </>
  );
}

export function EntPartnersChicagoReferralInstructions({
  referral,
  schedulingVerificationTaskEnabled,
}: EntPartnersReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <DefaultENTPReferralMessage
            referral={referral}
            groupName={"Chicago ENT"}
            showSchedulingRecommendation
            schedulingVerificationTaskEnabled={schedulingVerificationTaskEnabled}
          />
        </div>
      </EhrInstructionsCard>

      <EntPartnersReferralCards referral={referral} />
    </div>
  );
}

export function EntsReferralInstructions({ referral }: EntPartnersReferralInstructionsProps) {
  const formattedDateOfReferral = formatDateForFilename(referral.dateOfReferral);
  const documentLabel = `${formattedDateOfReferral} Referral to ${referral.serviceLine || "ENT"}`;
  const telEncMessage = buildEntsTelEncMessage(referral);

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <ValerieCard title="ENT Specialists Referral Instructions" className="border-ehr/20 bg-ehr/5 dark:bg-ehr/20">
        <div className="text-sm text-muted-foreground flex flex-col gap-2">
          <p>1. Create or update the patient in eCW.</p>
          <p>
            2. Upload the referral in Patient Docs, under the Referral folder. Name the file{" "}
            <CopyableContent text={documentLabel} /> and assign it to{" "}
            <CopyableContent text={ENTS_REFERRAL_ASSIGNEE} alwaysShow />. Mark it reviewed.
          </p>
          <p>
            3. Create a telephone encounter assigned to <CopyableContent text={ENTS_REFERRAL_ASSIGNEE} alwaysShow />{" "}
            with the reason <CopyableContent text={ENTS_TEL_ENC_REASON} /> and message{" "}
            <CopyableContent text={telEncMessage} />.
          </p>
        </div>
      </ValerieCard>
      <EntPartnersReferralCards referral={referral} />
    </div>
  );
}

export function SientReferralInstructions({ referral }: EntPartnersReferralInstructionsProps) {
  const formattedDateOfReferral = formatDateForFilename(referral.dateOfReferral);

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <div className="flex flex-col gap-1">
            <p className="font-medium text-center mb-4">Southern Indiana ENT Referral Instructions:</p>
            <div className="text-muted-foreground flex flex-col gap-2">
              <p>1. Create or Update the patient in eCW</p>
              <p>
                2. Upload the file in Patient Docs, under the Referral Notes folder, assigned to{" "}
                <CopyableContent text={SIENT_REFERRAL_ASSIGNEE} alwaysShow={true} />. Name the file{" "}
                <CopyableContent text={`${formattedDateOfReferral} Referral`} />. Do NOT mark reviewed.
              </p>
            </div>
          </div>
        </div>
      </EhrInstructionsCard>

      <EntPartnersReferralCards referral={referral} />
    </div>
  );
}

export function EntPartnersSubOtoReferralInstructions({ referral }: EntPartnersReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <DefaultENTPReferralMessage
            referral={referral}
            groupName={"SubOto"}
            assignedTo={ENTP_SUBOTO_REFERRAL_TEL_ENC_ASSIGNEE}
          />
        </div>
      </EhrInstructionsCard>

      <EntPartnersReferralCards referral={referral} />
    </div>
  );
}

export function EntPartnersSCIReferralInstructions({ referral }: EntPartnersReferralInstructionsProps) {
  const formattedDateOfReferral = formatDateForFilename(referral.dateOfReferral);
  const serviceLine = referral.serviceLine || "no service line";
  const fileName = `${formattedDateOfReferral} ${serviceLine} Referral`;
  const isNchNeurosciencesSync = isEntpSciNchNeurosciencesSyncReferral(referral);

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <div className="flex flex-col gap-2">
            <span className="font-medium mb-4">Specialty Care ENT Referral Instructions:</span>{" "}
            <div className="text-muted-foreground">
              <p>
                1. Create or Update the patient in eCW:{" "}
                <a href={ENTP_SCI_ECW_LOGIN_URL} target="_blank">
                  {ENTP_SCI_ECW_LOGIN_URL}
                </a>
              </p>
              <p>
                2. Attach the file in Patient Docs, under the Referrals folder.{" "}
                {isNchNeurosciencesSync ? (
                  <>
                    Name the file <CopyableContent text={fileName} />. <span className="font-semibold">DO NOT</span>{" "}
                    mark the file as reviewed.
                  </>
                ) : (
                  <>
                    Mark as reviewed. Name the file <CopyableContent text={fileName} />
                  </>
                )}
              </p>
              {isNchNeurosciencesSync && (
                <p>
                  3. Assign the file to{" "}
                  <CopyableContent text={ENTP_SCI_NCH_NEUROSCIENCES_SYNC.assignee} alwaysShow={true} />
                </p>
              )}
            </div>
          </div>
        </div>
      </EhrInstructionsCard>

      <EntPartnersReferralCards referral={referral} />
    </div>
  );
}

export function EntPartnersMentReferralInstructions({ referral }: EntPartnersReferralInstructionsProps) {
  const formattedDateOfReferral = formatDateForFilename(referral.dateOfReferral);
  const providerName = referral.receivingProvider?.familyName ? getPersonName(referral.receivingProvider) : "ENT";
  const teMessage = `Please schedule this patient. Referred to ${providerName} for ${referral.reasonForReferral || "diagnosis"}.`;

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <div className="flex flex-col gap-2">
            <span className="font-medium mb-4">Maryland ENT Referral Instructions:</span>{" "}
            <div className="text-muted-foreground">
              <p>
                1. Create or Update the patient in eCW:{" "}
                <a
                  href={MENT_ECW_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:bg-accent rounded p-1"
                >
                  Link
                </a>
              </p>
              <p>
                2. Attach the file in Patient Docs, under the Referrals folder. Assign to{" "}
                <CopyableContent text={MENT_REFERRAL_FRONT_OFFICE_ASSIGNEE} alwaysShow={true} />. Name the file{" "}
                <CopyableContent text={`${formattedDateOfReferral} ENT Referral`} />
              </p>
              <p>
                3. Create a telephone encounter assigned to{" "}
                <CopyableContent text={MENT_REFERRAL_FRONT_OFFICE_ASSIGNEE} alwaysShow={true} />:{" "}
                <CopyableContent text={teMessage} alwaysShow={true} /> Facility:{" "}
                <CopyableContent text={MENT_REFERRAL_FACILITY} alwaysShow={true} />
              </p>
            </div>
          </div>
        </div>
      </EhrInstructionsCard>

      <EntPartnersReferralCards referral={referral} />
    </div>
  );
}
